import { useCallback, useRef, type RefObject } from 'react';

/**
 * Mantém a seleção do contentEditable e permite comandos da toolbar
 * mesmo quando o editor não está focado.
 */
export function useRichEditorSelection(editorRef: RefObject<HTMLDivElement | null>) {
  const savedRange = useRef<Range | null>(null);

  const saveSelection = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    if (el.contains(range.commonAncestorContainer)) {
      savedRange.current = range.cloneRange();
    }
  }, [editorRef]);

  const focusAndRestoreSelection = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;

    el.focus({ preventScroll: true });

    const sel = window.getSelection();
    if (!sel) return;

    let range = savedRange.current;
    if (!range || !el.contains(range.commonAncestorContainer)) {
      range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      savedRange.current = range.cloneRange();
    }

    sel.removeAllRanges();
    sel.addRange(range);
  }, [editorRef]);

  /**
   * Insere HTML (ou texto puro, ex. `[[slug]]`) na seleção guardada,
   * sem depender do foco atual.
   */
  const insertHtmlAtSelection = useCallback(
    (html: string) => {
      const el = editorRef.current;
      if (!el) return;

      focusAndRestoreSelection();

      let range = savedRange.current;
      if (!range || !el.contains(range.commonAncestorContainer)) {
        range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
      }

      range.deleteContents();

      // Bbcode / texto sem tags: nó de texto — evita parsing HTML de `[[...]]`.
      const isPlainText = !/<[a-z][\s\S]*>/i.test(html);
      let lastNode: Node | null = null;

      if (isPlainText) {
        const textNode = document.createTextNode(html);
        range.insertNode(textNode);
        lastNode = textNode;
      } else {
        const fragment = range.createContextualFragment(html);
        lastNode = fragment.lastChild;
        range.insertNode(fragment);
      }

      if (lastNode) {
        const after = document.createRange();
        after.setStartAfter(lastNode);
        after.collapse(true);
        savedRange.current = after.cloneRange();
        const sel = window.getSelection();
        if (sel) {
          sel.removeAllRanges();
          sel.addRange(after);
        }
      }

      return true;
    },
    [editorRef, focusAndRestoreSelection]
  );

  return {
    savedRange,
    saveSelection,
    focusAndRestoreSelection,
    insertHtmlAtSelection,
  };
}
